import {message} from "antd";
import {SearchParamController, ResponsiveController} from "@clockout-dev/design-elements";
import ClockoutController from "../../core/clockout.controller";
import Inherit from "../../core/inherit";
import UploadsSimulatorDelegate from "./uploads-simulator.delegate";
import SorComposerDelegate from "./sor-composer.delegate";
import DocumentAuditDelegate from "./document-audit.delegate";
import {SCOPES} from "../../constants/scopes.constants";
import {AUDIT_TAB_KEY, DEFAULT_TAB, MODES, POLL_INTERVAL_MS, RUN_STATUS, RUN_TIMEOUT_MS, SIMULATOR_TAB_KEYS} from "./uploads-simulator.constants";
import {applyModeEntryDefaults, buildDefaultSor, mapSimulateError, normalizeSor, serializeSor, validateSor} from "./sor.model";
import {buildJobRowModel, buildMemberOptions, buildMemberSorApplication, mapAuditJobsError, mapContentError, mapMemberSorError} from "./audit.model";
import {deletePreset, listPresets, loadLastSor, saveLastSor, savePreset} from "./presets.storage";

const TAB_SEARCH_PARAM = "tab";
const LAST_SOR_DEBOUNCE_MS = 400;
const MEMBER_SEARCH_PAGE_SIZE = 8;

class UploadsSimulatorController extends Inherit(ClockoutController, UploadsSimulatorDelegate, SorComposerDelegate, DocumentAuditDelegate, SearchParamController, ResponsiveController) {
    /** Same <1024px threshold as reports: below it the results live in a modal. */
    get isSmallScreen() {
        return !!(this.state.isMobile || this.state.isTablet);
    }

    get canSimulate() {
        return this.hasScope(SCOPES.UPLOADS_SIMULATOR_RUN);
    }

    get isRunning() {
        const status = this.state.run?.status;
        return status === RUN_STATUS.UPLOADING || status === RUN_STATUS.RUNNING;
    }

    get messenger() {
        return this.props.messageApi ?? message;
    }

    async initialize() {
        if (!this.user || !this.user.access || this.user.access.length === 0) {
            this.navigateLogin();
            return;
        }

        // Subscribe to the breakpoint listeners before first meaningful paint
        // so the composer/results split starts in the right layout.
        this.mountResponsive();

        const initialTab = this.resolveInitialTab();
        this.setPresets(listPresets());

        // Restore the last authored SOR; when the browser has none, seed the
        // composer from the server's per-mode sample so the first run is a
        // one-click demo instead of a blank form. `audit` is not a mode —
        // deep-linking into it seeds the composer from the default mode.
        const lastSor = loadLastSor();
        if (lastSor) {
            this.setState({activeTab: initialTab, sor: applyModeEntryDefaults(normalizeSor(lastSor), initialTab)});
        } else {
            this.setState({activeTab: initialTab});
            await this.loadServerPreset(MODES[initialTab] ? initialTab : DEFAULT_TAB, {silent: true});
        }
    }

    /**
     * `Page` owns the `?tab=` search param; mirror its initial value into our
     * `activeTab` so mode-specific rendering is correct on first paint.
     * @returns {string}
     */
    resolveInitialTab() {
        const fromUrl = this.getSearchParamValue(TAB_SEARCH_PARAM);
        return SIMULATOR_TAB_KEYS.includes(fromUrl) ? fromUrl : DEFAULT_TAB;
    }

    /**
     * Tab switch = mode switch (except `audit`, which swaps the whole layout
     * for the document-audit view). Files reset (each mode has its own
     * accept/multiple rules) — the shared SOR and any completed report are
     * deliberately kept: the report is labeled with the mode it ran under.
     * The one SOR exception is the mode-entry defaults (`applyModeEntryDefaults`).
     * An in-flight run keeps polling; its progress panel names its own mode.
     * The audit tab's member/job selection survives round-trips the same way.
     * @param {string} key
     */
    handleTabChange(key) {
        if (!SIMULATOR_TAB_KEYS.includes(key) || key === this.state.activeTab) return;
        this.setActiveTab(key);
        this.applyModeEntrySorDefaults(key);
        this.resetFileZone();
        this.setSorErrors({});
        // The member search input is uncontrolled and remounts with the audit
        // panel — reset its results so an empty box never sits over stale rows
        // (the selected member + jobs deliberately survive the round trip).
        if (key === AUDIT_TAB_KEY) this.resetMemberSearch();
    }

    /**
     * Programmatic tab switch (e.g. audit → composer after loading a member
     * SOR). `Page` renders in controlled mode off our `activeTab`, so we
     * write the `?tab=` param ourselves and reuse the normal change path.
     * @param {string} key
     */
    switchToTab(key) {
        if (!SIMULATOR_TAB_KEYS.includes(key)) return;
        this.setSearchParamValue(TAB_SEARCH_PARAM, key);
        this.handleTabChange(key);
    }

    // ------------------------------------------------------------------
    // SOR editing + persistence
    // ------------------------------------------------------------------

    /**
     * Re-apply a mode's SOR entry defaults on tab entry (currently: the Full
     * Manual Flow always starts with `onboarding_complete` off). Writes state
     * and localStorage only when something was actually stripped.
     * @param {string} mode
     */
    applyModeEntrySorDefaults(mode) {
        const sor = applyModeEntryDefaults(this.state.sor, mode);
        if (sor === this.state.sor) return;
        this.setSor(sor);
        saveLastSor(sor);
    }

    /**
     * Section-level SOR update from the composer, with a debounced
     * write-through to localStorage so a reload never loses authored data.
     * @param {string} sectionKey
     * @param {*} value
     */
    handleSorSectionChange(sectionKey, value) {
        this.setSorSection(sectionKey, value);
        if (this._lastSorTimer) clearTimeout(this._lastSorTimer);
        this._lastSorTimer = setTimeout(() => {
            this._lastSorTimer = null;
            saveLastSor(this.state.sor);
        }, LAST_SOR_DEBOUNCE_MS);
    }

    handleResetSor() {
        const sor = buildDefaultSor();
        this.setSor(sor);
        this.setSorErrors({});
        this.setSelectedPresetName(null);
        this.setPresetNotes([]);
        this.setMemberSorSource(null);
        saveLastSor(sor);
    }

    /**
     * Pull the server's authored-SOR sample for a mode (`{mode, notes, sor}`).
     * @param {string} [mode] defaults to the active tab (`audit` has no
     *   preset, so it resolves to the default mode)
     * @param {{silent?: boolean}} [options] suppress the "no preset" toast
     */
    async loadServerPreset(mode, {silent = false} = {}) {
        const requested = mode ?? this.state.activeTab;
        const resolvedMode = MODES[requested] ? requested : DEFAULT_TAB;
        this.setSamplePresetLoading(true);
        try {
            const preset = await this.data.fetchSimulationPreset(resolvedMode);
            if (preset?.sor) {
                const sor = applyModeEntryDefaults(normalizeSor(preset.sor), resolvedMode);
                this.setSor(sor);
                this.setSorErrors({});
                this.setSelectedPresetName(null);
                this.setPresetNotes(Array.isArray(preset.notes) ? preset.notes : []);
                this.setMemberSorSource(null);
                saveLastSor(sor);
            } else if (!silent) {
                this.messenger.warning("No sample preset is available for this mode.");
            }
        } finally {
            this.setSamplePresetLoading(false);
        }
    }

    handleSelectPreset(name) {
        const preset = this.state.presets.find(entry => entry.name === name);
        if (!preset) return;
        const sor = applyModeEntryDefaults(normalizeSor(preset.sor), this.state.activeTab);
        this.setSor(sor);
        this.setSorErrors({});
        this.setSelectedPresetName(name);
        this.setPresetNotes([]);
        this.setMemberSorSource(null);
        saveLastSor(sor);
    }

    /** Overwrite the currently selected preset with the composer SOR. */
    handleSavePreset() {
        const name = this.state.selectedPresetName;
        if (!name) return;
        this.setPresets(savePreset(name, this.state.sor));
        this.messenger.success(`Preset "${name}" updated.`);
    }

    handleSaveAsPreset(name) {
        const trimmed = (name || "").trim();
        if (!trimmed) return;
        this.setPresets(savePreset(trimmed, this.state.sor));
        this.setSelectedPresetName(trimmed);
        // A member-loaded SOR is no longer "unsaved" once it has a preset name.
        this.setMemberSorSource(null);
        this.messenger.success(`Preset "${trimmed}" saved.`);
    }

    handleDeletePreset(name) {
        if (!name) return;
        this.setPresets(deletePreset(name));
        if (this.state.selectedPresetName === name) this.setSelectedPresetName(null);
        this.messenger.success(`Preset "${name}" deleted.`);
    }

    // ------------------------------------------------------------------
    // Run lifecycle
    // ------------------------------------------------------------------

    /**
     * File-count issues for the active mode; SOR issues come from
     * `validateSor`. Kept here so the view's validation summary and the Run
     * gate share one source of truth.
     * @returns {string[]}
     */
    getFileValidationMessages() {
        const modeConfig = MODES[this.state.activeTab];
        const count = this.state.files.length;
        if (!modeConfig) return [];
        if (count < modeConfig.minFiles) {
            return [modeConfig.minFiles === 1 ? "Add a document to run the simulation." : `Add at least ${modeConfig.minFiles} documents (${count} selected).`];
        }
        if (count > modeConfig.maxFiles) {
            return [`At most ${modeConfig.maxFiles} documents are allowed (${count} selected).`];
        }
        return [];
    }

    /** Validate → POST multipart → poll every 2s under a 5-minute watchdog. */
    async runSimulation() {
        if (this.isRunning || !this.canSimulate) return;

        const {sor, files, activeTab} = this.state;
        const validation = validateSor(sor, activeTab);
        this.setSorErrors(validation.errors);
        if (!validation.ok) {
            this.messenger.error("Fix the SOR issues highlighted in the composer first.");
            return;
        }
        const fileMessages = this.getFileValidationMessages();
        if (fileMessages.length) {
            this.messenger.error(fileMessages[0]);
            return;
        }

        // Token invalidates every callback (poll ticks, timers, upload
        // progress) from a superseded or cancelled run.
        this._runToken = (this._runToken || 0) + 1;
        const token = this._runToken;
        this._abortController = new AbortController();

        this.setRunError(null);
        this.setReport(null);
        this.setSelectedFileIndex(0);
        this.setRun({
            status: RUN_STATUS.UPLOADING,
            stage: "upload",
            mode: activeTab,
            simulationId: null,
            uploadPct: 0,
            elapsedMs: 0,
            startedAt: Date.now(),
            fileCount: files.length
        });
        this.startElapsedTimer(token);
        this.startWatchdog(token);

        try {
            const response = await this.data.simulateIntake({
                mode: activeTab,
                sor: serializeSor(sor),
                files,
                signal: this._abortController.signal,
                onUploadProgress: event => this.handleUploadProgress(token, event)
            });
            if (token !== this._runToken) return;

            const simulationId = response?.simulation_id;
            if (!simulationId) {
                this.finishRunWithError({kind: "unknown", title: "Simulation failed", detail: "The server did not return a simulation id."});
                return;
            }
            this.patchRun({status: RUN_STATUS.RUNNING, stage: "verify", simulationId, uploadPct: 100});
            this.schedulePoll(token, simulationId);
        } catch (error) {
            if (token !== this._runToken) return;
            this.finishRunWithError(mapSimulateError(error));
        }
    }

    handleUploadProgress(token, event) {
        if (token !== this._runToken) return;
        const pct = event?.total ? Math.round((event.loaded / event.total) * 100) : null;
        if (pct === null) return;
        this.patchRun({uploadPct: pct, stage: pct >= 100 ? "queue" : "upload"});
    }

    schedulePoll(token, simulationId) {
        this._pollTimer = setTimeout(() => this.pollRun(token, simulationId), POLL_INTERVAL_MS);
    }

    async pollRun(token, simulationId) {
        if (token !== this._runToken) return;
        try {
            const payload = await this.data.fetchSimulationRun(simulationId, {signal: this._abortController?.signal});
            if (token !== this._runToken) return;

            if (payload?.status === "completed") {
                this.finishRunWithReport(payload);
                return;
            }
            if (payload?.status === "failed") {
                this.finishRunWithError({kind: "failed", title: "Simulation failed", detail: payload?.error || "The verification engine reported a failure."});
                return;
            }
            this.schedulePoll(token, simulationId);
        } catch (error) {
            if (token !== this._runToken) return;
            const mapped = mapSimulateError(error);
            if (mapped.kind === "cancelled") return;
            this.finishRunWithError(mapped);
        }
    }

    finishRunWithReport(report) {
        this.clearRunTimers();
        this.setRun(null);
        this.setReport(report);
        if (this.isSmallScreen) this.setPanelOpenMobile(true);
    }

    finishRunWithError(error) {
        this.clearRunTimers();
        this.setRun(null);
        if (!error || error.kind === "cancelled") return;
        this.setRunError(error);
        if (this.isSmallScreen) this.setPanelOpenMobile(true);
    }

    /**
     * Abort the in-flight request + poll loop. The server may still finish
     * the simulation on its side, but simulations persist nothing, so the
     * abandoned result simply expires (15-minute TTL).
     */
    cancelRun() {
        if (!this.isRunning) return;
        this._runToken = (this._runToken || 0) + 1;
        this._abortController?.abort();
        this.clearRunTimers();
        this.setRun(null);
        this.messenger.info("Run cancelled. The server may still finish it, but nothing persists.");
    }

    startElapsedTimer(token) {
        this._elapsedTimer = setInterval(() => {
            if (token !== this._runToken) return;
            const startedAt = this.state.run?.startedAt;
            if (!startedAt) return;
            this.patchRun({elapsedMs: Date.now() - startedAt});
        }, 1000);
    }

    startWatchdog(token) {
        this._watchdogTimer = setTimeout(() => {
            if (token !== this._runToken) return;
            this._runToken += 1;
            this._abortController?.abort();
            // Same terminal path as every other error so small screens get
            // the auto-opened results modal too.
            this.finishRunWithError({kind: "timeout", title: "Simulation timed out", detail: "No result after 5 minutes — the run was abandoned client-side. Nothing persists server-side."});
        }, RUN_TIMEOUT_MS);
    }

    clearRunTimers() {
        if (this._pollTimer) clearTimeout(this._pollTimer);
        if (this._elapsedTimer) clearInterval(this._elapsedTimer);
        if (this._watchdogTimer) clearTimeout(this._watchdogTimer);
        this._pollTimer = null;
        this._elapsedTimer = null;
        this._watchdogTimer = null;
    }

    // ------------------------------------------------------------------
    // Document Audit — member search
    // ------------------------------------------------------------------

    /**
     * Fired by the audit tab's / member-modal's `DebouncedSearch`. A blank
     * term clears the result list without a fetch.
     * @param {string} term
     */
    handleMemberSearch(term) {
        const query = (term || "").trim();
        if (!query) {
            this.resetMemberSearch();
            return;
        }
        this.setMemberQuery(query);
        this.searchMembers(query);
    }

    /** Drop the query/result/loading slice and invalidate in-flight searches. */
    resetMemberSearch() {
        this._memberSearchToken = (this._memberSearchToken || 0) + 1;
        this.setMemberQuery("");
        this.setMemberResults([]);
        this.setMemberSearchLoading(false);
    }

    /**
     * One page of the SAME members endpoint the reports page uses
     * (`fetchUsers` swallows errors → `{}`, so a failure just reads as an
     * empty result list). A token drops out-of-order responses.
     * @param {string} query
     */
    async searchMembers(query) {
        this._memberSearchToken = (this._memberSearchToken || 0) + 1;
        const token = this._memberSearchToken;
        this.setMemberSearchLoading(true);
        try {
            const payload = await this.data.fetchUsers({search: query, page: 1, pageSize: MEMBER_SEARCH_PAGE_SIZE});
            if (token !== this._memberSearchToken) return;
            this.setMemberResults(buildMemberOptions(payload));
        } finally {
            if (token === this._memberSearchToken) this.setMemberSearchLoading(false);
        }
    }

    // ------------------------------------------------------------------
    // Document Audit — jobs + preview
    // ------------------------------------------------------------------

    /**
     * Row pick in the member search list: reset the job/preview selection
     * from any previous member and load the new member's intake jobs.
     * @param {{clientId: string, name?: string, email?: string}} member
     */
    async selectMember(member) {
        if (!member?.clientId) return;
        this._previewToken = (this._previewToken || 0) + 1;
        this._previewAbort?.abort();
        this.revokePreviewUrl();
        this.setSelectedMember(member);
        this.setSelectedJobId(null);
        this.setPreview({objectUrl: null, mimeType: null, loading: false, error: null});
        await this.loadIntakeJobs(member.clientId);
    }

    /**
     * Fetch the member's real intake jobs. RETHROWN data-layer errors are
     * mapped here (404 member-not-under-FI vs 403 vs network) so the panel
     * can render them distinctly.
     * @param {string} clientId
     */
    async loadIntakeJobs(clientId) {
        this._auditToken = (this._auditToken || 0) + 1;
        const token = this._auditToken;
        this._jobsAbort?.abort();
        this._jobsAbort = new AbortController();
        this.setJobs([]);
        this.setJobsError(null);
        this.setJobsLoading(true);
        try {
            const payload = await this.data.fetchIntakeJobs(clientId, {signal: this._jobsAbort.signal});
            if (token !== this._auditToken) return;
            this.setJobs(Array.isArray(payload?.jobs) ? payload.jobs : []);
            // The jobs endpoint is authoritative for the member's name.
            if (payload?.member_name && this.state.selectedMember?.clientId === clientId) {
                this.setSelectedMember({...this.state.selectedMember, name: payload.member_name});
            }
        } catch (error) {
            if (token !== this._auditToken) return;
            const mapped = mapAuditJobsError(error);
            if (mapped.kind === "cancelled") return;
            this.setJobsError(mapped);
        } finally {
            if (token === this._auditToken) this.setJobsLoading(false);
        }
    }

    /**
     * Row click in the uploads table. S3-retained jobs fetch the stored
     * bytes as a Blob and hold them behind an object URL; hash-only jobs
     * just clear the preview (the rail renders the sha256 receipt instead).
     * The previous object URL is always revoked first.
     * @param {string} jobId
     */
    async selectJob(jobId) {
        if (!jobId) return;
        // Re-clicking the selected row is a no-op EXCEPT after a failed
        // preview fetch — the error card tells the admin to select the row
        // again, so that path must re-fetch.
        if (jobId === this.state.selectedJobId && !this.state.preview?.error) return;
        this._previewToken = (this._previewToken || 0) + 1;
        const token = this._previewToken;
        this._previewAbort?.abort();
        this.revokePreviewUrl();
        this.setSelectedJobId(jobId);

        const job = (this.state.jobs || []).find(entry => entry?.id === jobId);
        if (!job || !buildJobRowModel(job).retained) {
            this.setPreview({objectUrl: null, mimeType: null, loading: false, error: null});
            return;
        }

        this._previewAbort = new AbortController();
        this.setPreview({objectUrl: null, mimeType: job.mime_type || null, loading: true, error: null});
        try {
            const blob = await this.data.fetchIntakeJobContent(this.state.selectedMember?.clientId, jobId, {signal: this._previewAbort.signal});
            if (token !== this._previewToken) return;
            if (!(blob instanceof Blob)) {
                this.setPreview({objectUrl: null, mimeType: null, loading: false, error: mapContentError(null)});
                return;
            }
            const objectUrl = URL.createObjectURL(blob);
            this._previewObjectUrl = objectUrl;
            this.setPreview({objectUrl, mimeType: job.mime_type || blob.type || null, loading: false, error: null});
        } catch (error) {
            if (token !== this._previewToken) return;
            const mapped = mapContentError(error);
            if (mapped.kind === "cancelled") return;
            this.setPreview({objectUrl: null, mimeType: null, loading: false, error: mapped});
        }
    }

    /** Revoke (and forget) the preview's object URL, if one is live. */
    revokePreviewUrl() {
        if (this._previewObjectUrl) {
            URL.revokeObjectURL(this._previewObjectUrl);
            this._previewObjectUrl = null;
        }
    }

    // ------------------------------------------------------------------
    // Member SOR → composer
    // ------------------------------------------------------------------

    /**
     * Fetch a real member's SOR and apply it through the SAME path the
     * server-preset loader uses (`normalizeSor → setSor` + last-SOR
     * write-through). Marks the preset bar with the unsaved member source
     * and surfaces `meta.notes[]` via the composer's notes strip.
     *
     * @param {string} clientId
     * @returns {Promise<boolean>} true when the SOR was applied
     */
    async loadMemberSor(clientId) {
        if (!clientId || this.state.memberSorLoading) return false;
        this.setMemberSorLoading(true);
        try {
            const payload = await this.data.fetchMemberSor(clientId);
            if (!payload?.sor) {
                this.messenger.error("The server returned no SOR for this member.");
                return false;
            }
            const application = buildMemberSorApplication(payload, clientId);
            this.setSor(application.sor);
            this.setSorErrors({});
            this.setSelectedPresetName(null);
            this.setPresetNotes(application.notes);
            this.setMemberSorSource(application.source);
            saveLastSor(application.sor);
            const name = application.source.memberName || clientId;
            const noteCount = application.notes.length;
            this.messenger.success(noteCount ? `Loaded ${name}'s SOR — ${noteCount} server note${noteCount === 1 ? "" : "s"} shown in the composer.` : `Loaded ${name}'s SOR into the composer.`);
            return true;
        } catch (error) {
            this.messenger.error(mapMemberSorError(error));
            return false;
        } finally {
            this.setMemberSorLoading(false);
        }
    }

    /** Open the "Load from member…" modal with a clean search slice. */
    openMemberSorModal() {
        this.resetMemberSearch();
        this.setMemberSorModalOpen(true);
    }

    /** Member picked inside the "Load from member…" modal. */
    async handleMemberSorModalPick(member) {
        const ok = await this.loadMemberSor(member?.clientId);
        if (ok) this.setMemberSorModalOpen(false);
    }

    /** Audit-rail button: apply the selected member's SOR, then jump to the composer. */
    async handleLoadMemberSorIntoComposer() {
        const member = this.state.selectedMember;
        if (!member?.clientId) return;
        const ok = await this.loadMemberSor(member.clientId);
        if (ok) this.switchToTab(DEFAULT_TAB);
    }

    // ------------------------------------------------------------------
    // Misc
    // ------------------------------------------------------------------

    /** Plain-text clipboard write (sha256, ids) with a labeled toast. */
    async handleCopyText(text, label = "Value") {
        try {
            await navigator.clipboard.writeText(String(text ?? ""));
            this.messenger.success(`${label} copied to clipboard.`);
        } catch (error) {
            this.messenger.error("Could not copy to the clipboard.");
        }
    }

    async handleCopyJson(data) {
        try {
            await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
            this.messenger.success("Copied JSON to clipboard.");
        } catch (error) {
            this.messenger.error("Could not copy to the clipboard.");
        }
    }

    /** Closes the mobile/tablet results modal (report stays for re-opening). */
    handleClosePanel() {
        this.setPanelOpenMobile(false);
    }

    async cleanup() {
        // Invalidate every pending callback, abort the in-flight request and
        // drop all timers — the page must leave nothing running behind it.
        this._runToken = (this._runToken || 0) + 1;
        this._abortController?.abort();
        this.clearRunTimers();
        if (this._lastSorTimer) {
            clearTimeout(this._lastSorTimer);
            this._lastSorTimer = null;
            saveLastSor(this.state.sor);
        }
        // Audit tab: invalidate search/jobs/preview callbacks, abort the
        // in-flight fetches and release the preview's object URL.
        this._memberSearchToken = (this._memberSearchToken || 0) + 1;
        this._auditToken = (this._auditToken || 0) + 1;
        this._previewToken = (this._previewToken || 0) + 1;
        this._jobsAbort?.abort();
        this._previewAbort?.abort();
        this.revokePreviewUrl();
        this.unmountResponsive();
    }
}

export default UploadsSimulatorController;
